import { type ComputedRef, computed, nextTick, onMounted, ref } from 'vue';
import {
  type MaybeComputedElementRef,
  tryOnScopeDispose,
  unrefElement,
  useResizeObserver,
} from '@vueuse/core';

import { HEADER_COLLAPSE_HYSTERESIS_PX } from './useHeaderCollapse';

/**
 * Order in which the contact details give things up as the header column runs out of room. The job
 * title always shrinks first — it is the one part of the row that truncates without being asked —
 * and then the values are allowed to ellipsize alongside it, until one of them would be narrower
 * than `MIN_CONTACT_VALUE_WIDTH_PX` and the phone and email fall back to their icon-only form. Kept
 * separate from the header row's ladder — same pattern, its own levels.
 */
export const ContactCollapseLevel = {
  None: 0,
  TruncateValues: 1,
  IconsOnly: 2,
} as const;

export const MAX_CONTACT_COLLAPSE_LEVEL = ContactCollapseLevel.IconsOnly;

/**
 * Narrowest a contact value is allowed to get before it drops to its icon — roughly seven or
 * eight characters of the row's `text-xs` face. Tunable.
 */
export const MIN_CONTACT_VALUE_WIDTH_PX = 56;

/** Marks the phone/email values, the parts of the row this ladder can give up. */
export const CONTACT_VALUE_TEST_ID = 'pro-header-contact-value';

/** Marks the candidate's job title, the part of the row that shrinks before anything is given up. */
export const CONTACT_TITLE_TEST_ID = 'pro-header-contact-title';

/**
 * Safety valve for the settling walk: one measurement per level it can cross, plus the one that
 * confirms it has stopped. The cap is only there so a pathological geometry cannot spin in a frame.
 */
const MAX_SETTLE_STEPS = MAX_CONTACT_COLLAPSE_LEVEL + 2;

export interface ContactCollapseInput {
  level: number;
  /**
   * Free space left once everything the row is currently cutting off is counted back in, so the
   * number means the same thing whether or not the values are allowed to ellipsize.
   */
  slack: number;
  /**
   * Free space the row would have with the values shown in full, measured while they were, so
   * bringing them back is priced from the layout it is bringing back rather than the one it is in.
   */
  expandedSlack?: number;
  /** Narrowest width any visible value currently has to work with. */
  narrowestValueWidth?: number;
  /** Width the row has right now. */
  available?: number;
  /**
   * Width the row is known to need before the values are worth showing again, learned while they
   * were on screen. It has to be a remembered measurement rather than a projection: the title and
   * both values are all shrinkable, so width handed back to the row is shared between them and the
   * narrowest value gets only a fraction of it. Projecting as if it got all of it promises far more
   * room than it gets, which is what made the row flicker.
   */
  widthForComfortableValues?: number;
  minValueWidth?: number;
  hysteresis?: number;
}

/**
 * Picks the level for the width the contact row currently has: one step per measurement, and a
 * step back only with the hysteresis buffer to spare, so the boundary cannot flicker.
 */
export function nextContactCollapseLevel({
  level,
  slack,
  expandedSlack,
  narrowestValueWidth,
  available,
  widthForComfortableValues,
  minValueWidth = MIN_CONTACT_VALUE_WIDTH_PX,
  hysteresis = HEADER_COLLAPSE_HYSTERESIS_PX,
}: ContactCollapseInput): number {
  if (level === ContactCollapseLevel.None) {
    // The row cannot show everything it holds, so let the values ellipsize.
    return slack < 0 ? ContactCollapseLevel.TruncateValues : ContactCollapseLevel.None;
  }

  if (level === ContactCollapseLevel.TruncateValues) {
    // An ellipsis does not make the row need less width, so this level is left by the room each
    // value actually has, not by the row's own shortfall.
    if (narrowestValueWidth !== undefined && narrowestValueWidth < minValueWidth) {
      return ContactCollapseLevel.IconsOnly;
    }

    return expandedSlack !== undefined && expandedSlack >= hysteresis
      ? ContactCollapseLevel.None
      : level;
  }

  // Bring the values back only once the row is as wide as they were last measured to need, so the
  // way back is priced from the layout that shows them rather than from the one hiding them.
  return available !== undefined &&
    widthForComfortableValues !== undefined &&
    available >= widthForComfortableValues
    ? ContactCollapseLevel.TruncateValues
    : ContactCollapseLevel.IconsOnly;
}

export interface ContactCollapseState {
  /** Let the values shrink and ellipsize rather than push the row wider. */
  truncateValues: ComputedRef<boolean>;
  /** Phone and email fall back to their icon-only form. */
  iconsOnly: ComputedRef<boolean>;
  remeasure: () => void;
  reset: () => void;
}

/**
 * The values the row is currently rendering. Visibility comes from the icon-only form actually
 * hiding them, not from their width: a value flexed all the way down to zero is still on the row
 * and still has to be measured, or the ladder would read it as absent and never reach the icons.
 */
function renderedValues(row: HTMLElement): HTMLElement[] {
  return (
    Array.from(row.querySelectorAll(`[data-test="${CONTACT_VALUE_TEST_ID}"]`)) as HTMLElement[]
  ).filter((value) => value.style.display !== 'none');
}

/** Everything the row is cutting off right now, whether with an ellipsis or by plain overflow. */
function clippedElements(row: HTMLElement): HTMLElement[] {
  const shrinkable = row.querySelectorAll(
    `[data-test="${CONTACT_TITLE_TEST_ID}"], [data-test="${CONTACT_VALUE_TEST_ID}"]`,
  );

  return (Array.from(shrinkable) as HTMLElement[]).filter(
    (element) => element.scrollWidth > element.clientWidth,
  );
}

function hiddenWidth(row: HTMLElement): number {
  return clippedElements(row).reduce(
    (total, element) => total + Math.max(0, element.scrollWidth - element.clientWidth),
    0,
  );
}

function contentWidth(row: HTMLElement): number {
  const items = Array.from(row.children) as HTMLElement[];
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) {
    return 0;
  }

  return last.offsetLeft + last.offsetWidth - first.offsetLeft;
}

/**
 * Collapses the contact details from what the row actually measures, the same way the header row
 * above it works, so it reacts to the panel being resized, to zoom, and to long values.
 */
export function useContactDetailsCollapse(row: MaybeComputedElementRef): ContactCollapseState {
  const level = ref<number>(ContactCollapseLevel.None);

  // Widths learned as the row visits each level. Every step back is priced from the level it
  // would land on rather than from the one the row is in, because the same row genuinely needs
  // different widths at different levels — pricing a step back from the current level's numbers is
  // what let the two directions disagree and flicker.
  const requiredWidthByLevel: (number | undefined)[] = new Array(
    MAX_CONTACT_COLLAPSE_LEVEL + 1,
  ).fill(undefined);

  // Narrowest row width the values were actually measured to be comfortable at, and — until one
  // has been seen — a deliberately cautious estimate of it. Width given back to the row is shared
  // between the title and both values, so the estimate assumes the narrowest value receives only
  // its share: erring towards holding the icons a little longer, never towards restoring early.
  let comfortableWidth: number | undefined;
  let estimatedComfortableWidth: number | undefined;

  let settling = false;
  let disposed = false;

  /** Whether the level moved, which is what tells the walk below it is not settled yet. */
  function measure(): boolean {
    const element = unrefElement(row);
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const available = element.clientWidth;
    // What the row would need to show everything in full: the width it currently lays out, plus
    // every pixel an ellipsis is hiding. Counting the hidden part back in is what makes this the
    // same number before and after the values are allowed to ellipsize — measure only the laid-out
    // width and the row appears to fit the moment it starts truncating, which is how the two
    // directions ended up disagreeing and flickering.
    const requiredWidth = contentWidth(element) + hiddenWidth(element);
    const slack = available - requiredWidth;
    const values = renderedValues(element);
    const narrowestValueWidth = values.length
      ? Math.min(...values.map((value) => value.clientWidth))
      : undefined;

    requiredWidthByLevel[level.value] = requiredWidth;

    if (narrowestValueWidth !== undefined) {
      const comfortableValueWidth = MIN_CONTACT_VALUE_WIDTH_PX + HEADER_COLLAPSE_HYSTERESIS_PX;

      if (narrowestValueWidth >= comfortableValueWidth) {
        // Measured: at this width the values are comfortable, so this is a width worth coming back
        // to. Keep the narrowest such width seen.
        comfortableWidth = Math.min(comfortableWidth ?? available, available);
      } else {
        // Not comfortable yet. Every clipped sibling competes for width handed to the row, so
        // assume the narrowest value only gets its share of what it is still missing.
        const sharing = Math.max(1, clippedElements(element).length);
        estimatedComfortableWidth =
          available + sharing * (comfortableValueWidth - narrowestValueWidth);
      }
    }

    const expandedRequiredWidth = requiredWidthByLevel[ContactCollapseLevel.None];

    const previousLevel = level.value;
    level.value = nextContactCollapseLevel({
      level: level.value,
      slack,
      expandedSlack:
        expandedRequiredWidth === undefined ? undefined : available - expandedRequiredWidth,
      narrowestValueWidth,
      available,
      widthForComfortableValues: comfortableWidth ?? estimatedComfortableWidth,
    });

    return level.value !== previousLevel;
  }

  /**
   * A level landing rearranges the row without necessarily resizing it, and a ResizeObserver only
   * reports size changes, so the walk has to keep itself going. It steps on Vue's own tick rather
   * than on an animation frame: the level reaches the DOM on the same microtask checkpoint that
   * queued it, so the whole walk runs inside the frame that started it and only the level it
   * settles on is ever painted. A frame at a time showed every level on the way, which is what made
   * candidate navigation flash the values in before dropping them back to their icons.
   */
  async function settle() {
    if (settling) {
      return;
    }

    settling = true;
    for (let step = 0; step < MAX_SETTLE_STEPS; step += 1) {
      await nextTick();
      if (disposed || !measure()) {
        break;
      }
    }
    settling = false;
  }

  function remeasure() {
    if (measure()) {
      void settle();
    }
  }

  function reset() {
    requiredWidthByLevel.fill(undefined);
    comfortableWidth = undefined;
    estimatedComfortableWidth = undefined;
    level.value = ContactCollapseLevel.None;
    void settle();
  }

  tryOnScopeDispose(() => {
    disposed = true;
  });

  useResizeObserver(row, remeasure);

  onMounted(async () => {
    await nextTick();
    remeasure();
  });

  return {
    truncateValues: computed(() => level.value >= ContactCollapseLevel.TruncateValues),
    iconsOnly: computed(() => level.value >= ContactCollapseLevel.IconsOnly),
    remeasure,
    reset,
  };
}
